import MasterProductsPageV2, {
  MasterProductsPageV2Props,
  ListingDisplay,
} from '@c/MasterProductsPageV2';
import MasterNotifyPopup from '@c/master/MasterNotifyPopup';
import { addCacheTag } from '@vercel/functions';
import { DefaultLayout } from '@c/layouts';
import { ParsedUrlQuery } from 'querystring';
import { getSimilarMasters } from '@util/firestore/master/master.service.server';
import { MasterDocument } from '@util/firestore/master/master.types';
import { getGearmasterDb } from '@util/gearmasterAdmin';
import { getProducts, ProductSortOption } from '@util/firestore/products/';
import { getSimilarListingsForMaster } from '@util/firestore/products/products.service.server';
import { BaseGetProductsArgs } from '@util/getPropsFromConext';
import { CategoryDocument, getCategories } from '@util/firestore/categories';
import { getConditions } from '@util/firestore/conditions/conditions.service';
import { getBrands } from 'pages/api/brands';
import { GetStaticPaths, GetStaticProps } from 'next';

interface Params extends ParsedUrlQuery {
  slugs: string[];
}

export default function MasterPage({
  master,
  results,
  similarMasters,
  similarListings,
}: MasterProductsPageV2Props) {
  return (
    <>
      <MasterProductsPageV2
        master={master}
        results={results}
        similarMasters={similarMasters}
        similarListings={similarListings}
      />
      <MasterNotifyPopup master={master} />
    </>
  );
}

MasterPage.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
});

/**
 * Mirrors getMasterById but throws on infra error so ISR serves the last good
 * page instead of caching a redirect; keep in sync with master.service.server.ts.
 *
 * Lookup order:
 * 1. Try by slug field (most common — URL-based lookups)
 * 2. Try by slugs array (handles alternate/legacy slugs)
 * 3. Try by document ID (handles old ID-based URLs for redirect)
 */
async function getMasterOrThrow(
  idOrSlug: string
): Promise<MasterDocument | null> {
  const db = getGearmasterDb();
  const col = db.collection('2.1');

  // 1. Try by primary slug
  const bySlug = await col
    .where('slug', '==', idOrSlug)
    .limit(1)
    .get();

  if (!bySlug.empty) {
    const doc = bySlug.docs[0];
    return { id: doc.id, ...doc.data() } as MasterDocument;
  }

  // 2. Try by slugs array (alternate slugs)
  const bySlugs = await col
    .where('slugs', 'array-contains', idOrSlug)
    .limit(1)
    .get();

  if (!bySlugs.empty) {
    const doc = bySlugs.docs[0];
    return { id: doc.id, ...doc.data() } as MasterDocument;
  }

  // 3. Try by document ID (legacy ID-based URLs). Skip when the segment can't be a valid
  // Firestore document ID (contains '/', is '.'/'..', reserved __x__, or > 1500 bytes) — a
  // malformed URL like /m/%2E%2E is a not-found (return null), NOT an infra error, and
  // col.doc() would otherwise throw synchronously and surface as a recurring 500. Only genuine
  // infrastructure errors from .get() propagate, so ISR still serves the last good page.
  const isValidDocId =
    !idOrSlug.includes('/') &&
    idOrSlug !== '.' &&
    idOrSlug !== '..' &&
    !/^__.*__$/.test(idOrSlug) &&
    Buffer.byteLength(idOrSlug) <= 1500;
  if (isValidDocId) {
    const byId = await col.doc(idOrSlug).get();
    if (byId.exists) {
      return { id: byId.id, ...byId.data() } as MasterDocument;
    }
  }

  return null;
}

export const getStaticProps: GetStaticProps<
  MasterProductsPageV2Props
> = async ({ params }) => {
  const { slugs } = params as Params;
  const master = await getMasterOrThrow(slugs[0]);

  if (master) {
    // Redirect to the canonical single-segment slug when the requested slug is non-canonical
    // OR the URL carries extra path segments (/m/<slug>/anything) — otherwise every junk suffix
    // generates and caches its own duplicate ISR entry. `revalidate` bounds how long the redirect
    // stays cached; omitting it caches the redirect until the next deploy, which can pin a stale
    // redirect loop across the corpus (old slug <-> new slug after slug standardization).
    if (slugs[0] !== master.slug || slugs.length > 1) {
      return {
        redirect: {
          destination: `/m/${master.slug}`,
          permanent: true
        },
        revalidate: 3600,
      };
    }
  } else {
    return {
      redirect: {
        destination: '/products',
        permanent: false
      },
      revalidate: 3600,
    };
  }

  // Build the canonical default product query args inline. getStaticProps has
  // no request query, so this replicates getProductQueryArgsFromContext's
  // output for an empty query (the ISR-cached page always shows page 1,
  // default sort, no filters — MasterProductsPageV2 has no filter/pagination
  // UI and does not refetch the grid client-side, so query params were vestigial).
  const [categories, conditions, brands] = await Promise.all([
    getCategories(),
    getConditions(),
    getBrands(),
  ]);

  const args = {
    limit: 25,
    sort: 'ranked' as ProductSortOption,
    is_expired: false,
    is_verified: false,
    keyword: null as string | null,
    page: 1,
    brandFilters: [] as string[],
    brandOptions: brands ?? [],
    conditionFilters: [] as string[],
    conditionOptions: conditions ?? [],
    categoryFilters: null as CategoryDocument | null,
    categoryOptions: categories ?? [],
    deals: [] as string[],
    useCases: [] as string[],
    maxPrice: null as number | null,
    minPrice: null as number | null,
    additionalFilters: {}
  } as BaseGetProductsArgs;

  // Fetch listings, similar masters, and similar listings in parallel
  const [masterProducts, similarMasters, similarListings] = await Promise.all([
    getProducts({ ...args, master: master.id }),
    getSimilarMasters({
      brand: master.brand,
      productType: master.productType,
      title: master.title,
      excludeId: master.id,
      // Precomputed ranking (GearBoard #1417) — when present this reads only
      // the neighbours actually rendered instead of scanning a candidate pool.
      similarMasterIds: master.similarMasterIds,
      similarMastersComputedAt: master.similarMastersComputedAt,
    }),
    getSimilarListingsForMaster(master.brand, master.categories ?? []),
  ]);

  // Add cache tags so listing create/edit (via invalidateByTag in /api/invalidate-cache) can
  // purge this ISR page. Awaited to avoid an unhandled floating promise.
  await addCacheTag(`master-${master.id}`);
  await addCacheTag('masters');

  // Strip internal/debug fields from master to reduce __NEXT_DATA__ payload.
  // All stripped fields verified unused by the MasterProductsPageV2 component tree.
  const {
    createdAt, updatedAt, enrichedAt, enrichedBy, gtinSource, gtinUpdatedAt,
    standardizationBatch, counter, series, vintage, videoId, faq, tags,
    slugs: _slugs, modelVariations, metadata, descriptionShort, features,
    standardizationRunId, standardizedAt, originalSlug, originalTitle,
    similarMasterIds, similarMastersComputedAt,
    ...trimmedMaster
  } = master as Record<string, unknown>;

  // Trim listings to display-only fields to reduce __NEXT_DATA__ payload.
  // Full ProductDocument has ~48 fields; components only need 28.
  const LISTING_KEYS = new Set<string>([
    'id', 'title', 'slug', 'thumbnail', 'images', 'price', 'og_price',
    'on_sale', 'out_of_stock', 'stock', 'condition', 'brand', 'categories',
    'sku', 'bumps', 'favorite_count', 'is_auction', 'start_time', 'end_time',
    'is_verified', 'is_flat_rate', 'seller_id', 'shipping_costs', 'gearMaster',
    'external_source', 'integrations', 'description', 'account_id',
  ]);
  const trimListing = (l: Record<string, unknown>): ListingDisplay => {
    const t: Record<string, unknown> = {};
    for (const k of LISTING_KEYS) if (k in l) t[k] = l[k];
    return t as ListingDisplay;
  };

  return {
    props: {
      master: trimmedMaster as MasterProductsPageV2Props['master'],
      results: masterProducts.results.map(r => trimListing(r as Record<string, unknown>)),
      // Trim similarMasters to display-only fields (7 of 46) — saves ~93 KB
      similarMasters: similarMasters.map(({ id, slug, images, thumbnail, title, brand, price }) => ({
        id, slug, images, thumbnail, title, brand, price,
      })),
      similarListings: similarListings.map(l => trimListing(l as Record<string, unknown>)),
    },
    // 1h — matches the current live edge TTL, so no freshness regression vs today. Raise to
    // 43200 (12h) only AFTER gearfocus-api busts master-<id> tags on sale/out_of_stock; without
    // that, backend-sourced changes (sales, UPP, offers) would ghost on /m/ for up to 12h.
    revalidate: 3600,
  };
};
